import React, { useState, useEffect, useReducer, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Box,
  Avatar,
  Typography,
  Tooltip,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  TableContainer,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Info as InfoIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  FileCopy as CopyIcon,
  ZoomIn as ZoomInIcon,
  AutorenewRounded as EnrichIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ProductImportModal from "../../components/ProductImportModal";
import ProductImageModal from "../../components/ProductImageModal";
import * as pharmaApi from "../../services/pharmaApi";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_PRODUCTS":
      return [...action.payload];
    case "UPDATE_PRODUCT":
      const product = action.payload;
      const productIndex = state.findIndex((p) => p.id === product.id);
      if (productIndex !== -1) {
        state[productIndex] = product;
        return [...state];
      }
      return [product, ...state];
    case "DELETE_PRODUCT":
      return state.filter((p) => p.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    ...theme.scrollbarStyles,
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchField: {
    minWidth: 300,
    flex: 1,
    maxWidth: 500,
  },
  productImage: {
    width: 50,
    height: 50,
    borderRadius: 8,
    border: "1px solid #e0e0e0",
  },
  productImageLarge: {
    width: 120,
    height: 120,
    borderRadius: 8,
    border: "1px solid #e0e0e0",
  },
  prescriptionChip: {
    fontSize: 11,
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  inactiveChip: {
    backgroundColor: "#9e9e9e",
    color: "#fff",
  },
  promoChip: {
    backgroundColor: "#ff5722",
    color: "#fff",
    fontSize: 10,
    marginLeft: 4,
  },
  featuredChip: {
    backgroundColor: "#ffc107",
    color: "#000",
    fontSize: 10,
    marginLeft: 4,
  },
  infoText: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "#2d3748" : "#f5f5f5",
    borderRadius: 8,
    fontSize: 13,
  },
  stockCell: {
    fontWeight: 500,
  },
  lowStock: {
    color: "#f44336",
  },
  normalStock: {
    color: "#4caf50",
  },
  productName: {
    fontWeight: 500,
  },
  productDetails: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  barcodeText: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    fontFamily: "monospace",
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  detailSection: {
    marginBottom: theme.spacing(2),
  },
  detailSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  detailLabel: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  detailValue: {
    fontWeight: 400,
    fontSize: 14,
  },
  detailRow: {
    marginBottom: theme.spacing(1),
  },
  tableContainer: {
    maxHeight: "calc(100vh - 350px)",
    width: "100%",
    overflowX: "auto",
  },
  tableHeaderCell: {
    fontWeight: 600,
    backgroundColor: theme.palette.type === "dark" ? "#2d3748" : "#f5f5f5",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  priceCell: {
    whiteSpace: "nowrap",
  },
  manufacturerText: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  copyButton: {
    color: theme.palette.success.main,
  },
  imagePreviewDialog: {
    "& .MuiDialog-paper": {
      backgroundColor: "transparent",
      boxShadow: "none",
      overflow: "visible",
      maxWidth: "fit-content",
      width: "auto",
      margin: 16,
    },
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(0, 0, 0, 0.85)",
    },
  },
  imagePreviewContent: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: 8,
    position: "relative",
    overflow: "visible",
    "&:first-child": {
      paddingTop: 0,
    },
  },
  imagePreviewImg: {
    maxWidth: "85vw",
    maxHeight: "75vh",
    objectFit: "contain",
    borderRadius: 8,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
  },
  imagePreviewClose: {
    position: "absolute",
    top: 8,
    right: 8,
    color: "#fff",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1,
    "&:hover": {
      backgroundColor: "rgba(255, 0, 0, 0.8)",
    },
  },
  productImageClickable: {
    cursor: "pointer",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  enrichmentStats: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  statCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "#2d3748" : "#f5f5f5",
  },
  statValue: {
    fontSize: 24,
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  progressContainer: {
    marginTop: theme.spacing(2),
  },
  enrichmentButton: {
    marginRight: 8,
  },
  // Enrichment status markers
  enrichmentChip: {
    fontSize: 10,
    height: 20,
    marginLeft: 4,
  },
  enrichmentSuccess: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  enrichmentPending: {
    backgroundColor: "#ff9800",
    color: "#fff",
  },
  enrichmentNotFound: {
    backgroundColor: "#9e9e9e",
    color: "#fff",
  },
  enrichmentError: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
  enrichmentNoBarcode: {
    backgroundColor: "#607d8b",
    color: "#fff",
  },
  enrichmentStatusFilter: {
    minWidth: 180,
  },
}));

const prescriptionLabels = {
  none: "Livre",
  simple: "Receita Simples",
  special: "Receita Especial",
  controlled_c1: "Controlado C1",
  controlled_c2: "Controlado C2",
  controlled_c3: "Controlado C3",
  controlled_c4: "Controlado C4",
  controlled_c5: "Controlado C5",
};

const prescriptionColors = {
  none: "default",
  simple: "primary",
  special: "secondary",
  controlled_c1: "secondary",
  controlled_c2: "secondary",
  controlled_c3: "secondary",
  controlled_c4: "secondary",
  controlled_c5: "secondary",
};

const productTypeLabels = {
  medication: "Medicamento",
  generic: "Genérico",
  similar: "Similar",
  cosmetic: "Cosmético",
  hygiene: "Higiene",
  other: "Outros",
};

const enrichmentStatusLabels = {
  pending: "Pendente",
  success: "Enriquecido",
  not_found: "Não Encontrado",
  error: "Erro",
  no_barcode: "Sem EAN",
};

const enrichmentStatusIcons = {
  pending: "?",
  success: "✓",
  not_found: "✗",
  error: "!",
  no_barcode: "-",
};

// Componente Modal de Detalhes do Produto
const ProductDetailModal = ({ open, onClose, product, classes, formatCurrency }) => {
  if (!product) return null;

  const DetailRow = ({ label, value }) => (
    <Grid item xs={12} sm={6} className={classes.detailRow}>
      <Typography className={classes.detailLabel}>{label}</Typography>
      <Typography className={classes.detailValue}>{value || "-"}</Typography>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">Detalhes do Produto</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Imagem e Info Básica */}
          <Grid item xs={12} sm={3}>
            <Avatar
              src={product.imageUrl}
              variant="rounded"
              className={classes.productImageLarge}
            >
              {product.name?.charAt(0)}
            </Avatar>
            <Box mt={1}>
              {product.isActive ? (
                <Chip label="Ativo" size="small" className={classes.activeChip} />
              ) : (
                <Chip label="Inativo" size="small" className={classes.inactiveChip} />
              )}
              {product.isFeatured && (
                <Chip label="Destaque" size="small" className={classes.featuredChip} style={{ marginLeft: 4 }} />
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography variant="h6" gutterBottom>{product.name}</Typography>
            {product.description && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {product.description}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* Identificação */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Identificação</Typography>
          </Grid>
          <DetailRow label="Código de Barras (EAN)" value={product.barcode} />
          <DetailRow label="SKU" value={product.sku} />
          <DetailRow label="ID Externo (ERP)" value={product.externalId} />
          <DetailRow label="Grupo" value={product.category?.name} />

          <Grid item xs={12}><Divider /></Grid>

          {/* Informações Farmacêuticas */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Informações Farmacêuticas</Typography>
          </Grid>
          <DetailRow label="Subgrupo" value={productTypeLabels[product.productType] || product.productType} />
          <DetailRow label="Fórmula" value={product.activePrinciple} />
          <DetailRow label="Fabricante" value={product.manufacturer} />
          <DetailRow label="Dosagem" value={product.dosage} />
          <DetailRow label="Apresentação" value={product.presentation} />
          <DetailRow label="Registro MS" value={product.msRegistry} />
          <DetailRow label="Receita" value={prescriptionLabels[product.requiresPrescription] || "Livre"} />

          <Grid item xs={12}><Divider /></Grid>

          {/* Preços */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Preços</Typography>
          </Grid>
          <DetailRow label="Preço de Custo" value={product.costPrice ? formatCurrency(product.costPrice) : "-"} />
          <DetailRow label="Preço de Venda" value={formatCurrency(product.price)} />
          <DetailRow label="Preço Máximo (PMC)" value={product.maxPrice ? formatCurrency(product.maxPrice) : "-"} />
          <DetailRow label="Preço Mínimo Venda" value={product.minSalePrice ? formatCurrency(product.minSalePrice) : "-"} />

          <Grid item xs={12}><Divider /></Grid>

          {/* Desconto */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Configurações de Desconto</Typography>
          </Grid>
          <DetailRow label="Permite Desconto" value={product.allowsDiscount ? "Sim" : "Não"} />
          <DetailRow label="% Máximo Desconto" value={product.maxDiscountPercent ? `${product.maxDiscountPercent}%` : "-"} />
          <DetailRow label="Requer Autorização" value={product.discountRequiresAuth ? "Sim" : "Não"} />

          <Grid item xs={12}><Divider /></Grid>

          {/* Estoque */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Estoque</Typography>
          </Grid>
          <DetailRow
            label="Quantidade Disponível"
            value={product.stock?.availableQuantity ?? product.stock?.quantity ?? "-"}
          />
          <DetailRow
            label="Quantidade Reservada"
            value={product.stock?.reservedQuantity ?? "-"}
          />
          <DetailRow
            label="Estoque Mínimo"
            value={product.stock?.minQuantity ?? "-"}
          />

          <Grid item xs={12}><Divider /></Grid>

          {/* Fiscal */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Informações Fiscais</Typography>
          </Grid>
          <DetailRow label="NCM" value={product.ncm} />
          <DetailRow label="CEST" value={product.cest} />
          <DetailRow label="Categoria Fiscal" value={product.taxCategory} />

          <Grid item xs={12}><Divider /></Grid>

          {/* Dimensões */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Dimensões e Peso</Typography>
          </Grid>
          <DetailRow label="Peso (g)" value={product.weightGrams} />
          <DetailRow label="Largura (cm)" value={product.widthCm} />
          <DetailRow label="Altura (cm)" value={product.heightCm} />
          <DetailRow label="Profundidade (cm)" value={product.depthCm} />

          <Grid item xs={12}><Divider /></Grid>

          {/* Datas */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Datas</Typography>
          </Grid>
          <DetailRow
            label="Criado em"
            value={product.createdAt ? new Date(product.createdAt).toLocaleString("pt-BR") : "-"}
          />
          <DetailRow
            label="Atualizado em"
            value={product.updatedAt ? new Date(product.updatedAt).toLocaleString("pt-BR") : "-"}
          />
          <DetailRow
            label="Sincronizado em"
            value={product.syncedAt ? new Date(product.syncedAt).toLocaleString("pt-BR") : "-"}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Modal de Adicionar/Editar Produto
const ProductFormModal = ({ open, onClose, product, categories, onSave, classes, onManageImage }) => {
  const isEditing = !!product?.id;
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    barcode: "",
    sku: "",
    description: "",
    categoryId: "",
    productType: "other",
    activePrinciple: "",
    manufacturer: "",
    dosage: "",
    presentation: "",
    requiresPrescription: "none",
    msRegistry: "",
    costPrice: "",
    price: "",
    maxPrice: "",
    minSalePrice: "",
    allowsDiscount: true,
    maxDiscountPercent: "",
    discountRequiresAuth: false,
    ncm: "",
    cest: "",
    taxCategory: "",
    weightGrams: "",
    widthCm: "",
    heightCm: "",
    depthCm: "",
    imageUrl: "",
    isActive: true,
    isFeatured: false,
    stockQuantity: "",
  });

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || "",
        barcode: product.barcode || "",
        sku: product.sku || "",
        description: product.description || "",
        categoryId: product.categoryId || "",
        productType: product.productType || "other",
        activePrinciple: product.activePrinciple || "",
        manufacturer: product.manufacturer || "",
        dosage: product.dosage || "",
        presentation: product.presentation || "",
        requiresPrescription: product.requiresPrescription || "none",
        msRegistry: product.msRegistry || "",
        costPrice: product.costPrice || "",
        price: product.price || "",
        maxPrice: product.maxPrice || "",
        minSalePrice: product.minSalePrice || "",
        allowsDiscount: product.allowsDiscount ?? true,
        maxDiscountPercent: product.maxDiscountPercent || "",
        discountRequiresAuth: product.discountRequiresAuth || false,
        ncm: product.ncm || "",
        cest: product.cest || "",
        taxCategory: product.taxCategory || "",
        weightGrams: product.weightGrams || "",
        widthCm: product.widthCm || "",
        heightCm: product.heightCm || "",
        depthCm: product.depthCm || "",
        imageUrl: product.imageUrl || "",
        isActive: product.isActive ?? true,
        isFeatured: product.isFeatured || false,
        stockQuantity: product.stock?.quantity || product.stock?.availableQuantity || "",
      });
    } else {
      setFormData({
        name: "",
        barcode: "",
        sku: "",
        description: "",
        categoryId: "",
        productType: "other",
        activePrinciple: "",
        manufacturer: "",
        dosage: "",
        presentation: "",
        requiresPrescription: "none",
        msRegistry: "",
        costPrice: "",
        price: "",
        maxPrice: "",
        minSalePrice: "",
        allowsDiscount: true,
        maxDiscountPercent: "",
        discountRequiresAuth: false,
        ncm: "",
        cest: "",
        taxCategory: "",
        weightGrams: "",
        widthCm: "",
        heightCm: "",
        depthCm: "",
        imageUrl: "",
        isActive: true,
        isFeatured: false,
        stockQuantity: "",
      });
    }
  }, [product, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.price) {
      toast.error("Nome e Preço são obrigatórios");
      return;
    }

    setSaving(true);
    try {
      await onSave(formData, product?.id);
      onClose();
    } catch (err) {
      toast.error(err.message || "Erro ao salvar produto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">{isEditing ? "Editar Produto" : "Novo Produto"}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Informações Básicas */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Informações Básicas</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Nome do Produto *"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="barcode"
              label="Código de Barras (EAN)"
              value={formData.barcode}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="sku"
              label="SKU"
              value={formData.sku}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="description"
              label="Descrição"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Grupo</InputLabel>
              <Select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                label="Grupo"
              >
                <MenuItem value="">Nenhum</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Subgrupo</InputLabel>
              <Select
                name="productType"
                value={formData.productType}
                onChange={handleChange}
                label="Subgrupo"
              >
                <MenuItem value="medication">Medicamento</MenuItem>
                <MenuItem value="generic">Genérico</MenuItem>
                <MenuItem value="similar">Similar</MenuItem>
                <MenuItem value="cosmetic">Cosmético</MenuItem>
                <MenuItem value="hygiene">Higiene</MenuItem>
                <MenuItem value="other">Outros</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* Informações Farmacêuticas */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Informações Farmacêuticas</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="activePrinciple"
              label="Fórmula"
              value={formData.activePrinciple}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="manufacturer"
              label="Fabricante"
              value={formData.manufacturer}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="dosage"
              label="Dosagem"
              value={formData.dosage}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="presentation"
              label="Apresentação"
              value={formData.presentation}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="msRegistry"
              label="Registro MS"
              value={formData.msRegistry}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Tipo de Receita</InputLabel>
              <Select
                name="requiresPrescription"
                value={formData.requiresPrescription}
                onChange={handleChange}
                label="Tipo de Receita"
              >
                <MenuItem value="none">Livre (Sem Receita)</MenuItem>
                <MenuItem value="simple">Receita Simples</MenuItem>
                <MenuItem value="special">Receita Especial</MenuItem>
                <MenuItem value="controlled_c1">Controlado C1</MenuItem>
                <MenuItem value="controlled_c2">Controlado C2</MenuItem>
                <MenuItem value="controlled_c3">Controlado C3</MenuItem>
                <MenuItem value="controlled_c4">Controlado C4</MenuItem>
                <MenuItem value="controlled_c5">Controlado C5</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* Preços */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Preços</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              name="costPrice"
              label="Preço de Custo"
              value={formData.costPrice}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              name="price"
              label="Preço de Venda *"
              value={formData.price}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              name="maxPrice"
              label="Preço Máximo (PMC)"
              value={formData.maxPrice}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              name="minSalePrice"
              label="Preço Mínimo"
              value={formData.minSalePrice}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* Estoque */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Estoque</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="stockQuantity"
              label="Quantidade em Estoque"
              value={formData.stockQuantity}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ min: "0" }}
            />
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* Desconto */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Configurações de Desconto</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Permite Desconto</InputLabel>
              <Select
                name="allowsDiscount"
                value={formData.allowsDiscount}
                onChange={(e) => setFormData(prev => ({ ...prev, allowsDiscount: e.target.value === "true" || e.target.value === true }))}
                label="Permite Desconto"
              >
                <MenuItem value={true}>Sim</MenuItem>
                <MenuItem value={false}>Não</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="maxDiscountPercent"
              label="% Máximo Desconto"
              value={formData.maxDiscountPercent}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ step: "0.01", min: "0", max: "100" }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Requer Autorização</InputLabel>
              <Select
                name="discountRequiresAuth"
                value={formData.discountRequiresAuth}
                onChange={(e) => setFormData(prev => ({ ...prev, discountRequiresAuth: e.target.value === "true" || e.target.value === true }))}
                label="Requer Autorização"
              >
                <MenuItem value={false}>Não</MenuItem>
                <MenuItem value={true}>Sim</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* Fiscal */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Informações Fiscais</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="ncm"
              label="NCM"
              value={formData.ncm}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="cest"
              label="CEST"
              value={formData.cest}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="taxCategory"
              label="Categoria Fiscal"
              value={formData.taxCategory}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* Dimensões */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Dimensões e Peso</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              name="weightGrams"
              label="Peso (g)"
              value={formData.weightGrams}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              type="number"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              name="widthCm"
              label="Largura (cm)"
              value={formData.widthCm}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ step: "0.01" }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              name="heightCm"
              label="Altura (cm)"
              value={formData.heightCm}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ step: "0.01" }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              name="depthCm"
              label="Profundidade (cm)"
              value={formData.depthCm}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ step: "0.01" }}
            />
          </Grid>

          <Grid item xs={12}><Divider /></Grid>

          {/* Imagem e Status */}
          <Grid item xs={12}>
            <Typography className={classes.detailSectionTitle}>Imagem e Status</Typography>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" style={{ gap: 16 }}>
              {formData.imageUrl && (
                <Avatar
                  src={formData.imageUrl}
                  variant="rounded"
                  style={{ width: 60, height: 60 }}
                >
                  <ImageIcon />
                </Avatar>
              )}
              <TextField
                name="imageUrl"
                label="URL da Imagem"
                value={formData.imageUrl}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                size="small"
                style={{ flex: 1 }}
              />
              {isEditing && onManageImage && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ImageIcon />}
                  onClick={() => onManageImage(product)}
                  style={{ whiteSpace: "nowrap" }}
                >
                  Gerenciar
                </Button>
              )}
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="isActive"
                value={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === "true" || e.target.value === true }))}
                label="Status"
              >
                <MenuItem value={true}>Ativo</MenuItem>
                <MenuItem value={false}>Inativo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Destaque</InputLabel>
              <Select
                name="isFeatured"
                value={formData.isFeatured}
                onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.value === "true" || e.target.value === true }))}
                label="Destaque"
              >
                <MenuItem value={false}>Não</MenuItem>
                <MenuItem value={true}>Sim</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={saving}>
          {saving ? "Salvando..." : isEditing ? "Salvar Alterações" : "Criar Produto"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Modal de Enriquecimento de Dados (Bluesoft Cosmos)
const ProductEnrichmentModal = ({ open, onClose, classes }) => {
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [stats, setStats] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      loadStats();
    }
  }, [open]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const data = await pharmaApi.getEnrichmentStats();
      setStats(data);
    } catch (err) {
      toast.error("Erro ao carregar estatísticas de enriquecimento");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRunEnrichment = async () => {
    setRunning(true);
    setResult(null);
    try {
      const data = await pharmaApi.runEnrichment();
      setResult(data);
      await loadStats(); // Refresh stats
      if (data.enriched > 0) {
        toast.success(`${data.enriched} produto(s) enriquecido(s) com sucesso!`);
      } else if (data.error) {
        toast.warning(data.error);
      } else {
        toast.info("Nenhum produto novo foi enriquecido");
      }
    } catch (err) {
      toast.error("Erro ao executar enriquecimento");
      console.error(err);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">Enriquecimento de Dados</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box className={classes.infoText}>
          <InfoIcon fontSize="small" />
          <Typography variant="body2">
            O enriquecimento usa a API Bluesoft Cosmos para buscar informações adicionais dos produtos (descrição, fabricante, NCM, imagem). Limite: 25 consultas/dia.
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : stats ? (
          <Box className={classes.enrichmentStats}>
            <Box className={classes.statCard}>
              <Box>
                <Typography className={classes.statLabel}>Total de Produtos</Typography>
                <Typography className={classes.statValue}>{stats.totalProducts}</Typography>
              </Box>
              <Box textAlign="right">
                <Typography className={classes.statLabel}>Enriquecidos</Typography>
                <Typography className={classes.statValue} style={{ color: "#4caf50" }}>
                  {stats.enrichedProducts} ({stats.enrichmentPercentage}%)
                </Typography>
              </Box>
            </Box>

            <Box className={classes.statCard}>
              <Box>
                <Typography className={classes.statLabel}>Pendentes</Typography>
                <Typography className={classes.statValue} style={{ color: "#ff9800" }}>
                  {stats.needingEnrichment}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography className={classes.statLabel}>Consultas Hoje</Typography>
                <Typography className={classes.statValue}>
                  {stats.todayUsage} / {stats.dailyLimit}
                </Typography>
              </Box>
            </Box>

            <Box className={classes.statCard}>
              <Box>
                <Typography className={classes.statLabel}>Disponíveis Hoje</Typography>
                <Typography className={classes.statValue} style={{ color: stats.todayRemaining > 0 ? "#2196f3" : "#f44336" }}>
                  {stats.todayRemaining}
                </Typography>
              </Box>
            </Box>

            {result && (
              <Box className={classes.statCard} style={{ backgroundColor: result.error ? "#fff3e0" : "#e8f5e9" }}>
                <Box>
                  <Typography className={classes.statLabel}>Último Resultado</Typography>
                  <Typography variant="body2">
                    Processados: {result.processed} | Enriquecidos: {result.enriched} | Falhas: {result.failed}
                  </Typography>
                  {result.error && (
                    <Typography variant="body2" color="error">{result.error}</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Typography color="textSecondary" align="center">
            Não foi possível carregar estatísticas
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Fechar
        </Button>
        <Button
          onClick={handleRunEnrichment}
          color="primary"
          variant="contained"
          disabled={running || loading || (stats && stats.todayRemaining <= 0)}
          startIcon={running ? <CircularProgress size={16} /> : <EnrichIcon />}
        >
          {running ? "Processando..." : "Executar Enriquecimento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PdvProducts = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [products, dispatch] = useReducer(reducer, []);
  const [categories, setCategories] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("true"); // Default: mostrar só ativos
  const [prescriptionFilter, setPrescriptionFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockFilter, setStockFilter] = useState(""); // "", "with", "without"
  const [productTypeFilter, setProductTypeFilter] = useState(""); // Subgrupo filter
  const [formulaFilter, setFormulaFilter] = useState(""); // Fórmula filter
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [enrichmentModalOpen, setEnrichmentModalOpen] = useState(false);
  const [enrichmentStatusFilter, setEnrichmentStatusFilter] = useState(""); // "", "pending", "success", "not_found", "error", "no_barcode"

  const handleOpenDetail = (product) => {
    setSelectedProduct(product);
    setDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailModalOpen(false);
    setSelectedProduct(null);
  };

  const handleOpenForm = (product = null) => {
    setSelectedProduct(product);
    setFormModalOpen(true);
  };

  const handleCloseForm = () => {
    setFormModalOpen(false);
    setSelectedProduct(null);
  };

  const handleOpenDelete = (product) => {
    setSelectedProduct(product);
    setDeleteModalOpen(true);
  };

  const handleCloseDelete = (confirmed) => {
    // ConfirmationModal passa false quando cancela
    if (confirmed === false || confirmed === undefined) {
      setDeleteModalOpen(false);
      setSelectedProduct(null);
    }
  };

  const handleOpenImage = (product) => {
    setSelectedProduct(product);
    setImageModalOpen(true);
  };

  const handleCloseImage = () => {
    setImageModalOpen(false);
    // Não limpa selectedProduct aqui para não interferir com o ProductFormModal que pode estar aberto
  };

  const handleImageUpdate = (updatedProduct) => {
    dispatch({ type: "UPDATE_PRODUCT", payload: updatedProduct });
    // Atualiza também o selectedProduct para refletir a mudança no ProductFormModal
    setSelectedProduct(prev => prev && prev.id === updatedProduct.id ? updatedProduct : prev);
  };

  const handleOpenImagePreview = (product, e) => {
    e.stopPropagation();
    if (product.imageUrl) {
      setPreviewProduct(product);
      setImagePreviewOpen(true);
    }
  };

  const handleCloseImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewProduct(null);
  };

  const handleSaveProduct = async (formData, productId) => {
    try {
      if (productId) {
        // Editar
        const updated = await pharmaApi.updateProduct(productId, formData);
        dispatch({ type: "UPDATE_PRODUCT", payload: updated });
        toast.success("Produto atualizado com sucesso!");
      } else {
        // Criar
        const created = await pharmaApi.createProduct(formData);
        dispatch({ type: "UPDATE_PRODUCT", payload: created });
        toast.success("Produto criado com sucesso!");
      }
    } catch (err) {
      toastError(err);
      throw err;
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;
    try {
      await pharmaApi.deleteProduct(selectedProduct.id);
      dispatch({ type: "DELETE_PRODUCT", payload: selectedProduct.id });
      toast.success("Produto excluído com sucesso!");
      handleCloseDelete();
    } catch (err) {
      toastError(err);
    }
  };

  // Load filter defaults from settings
  useEffect(() => {
    const loadFilterSettings = async () => {
      try {
        const settings = await pharmaApi.getSettings();
        if (settings) {
          // Apply saved filter defaults
          if (settings.productsDefaultActiveFilter !== undefined) {
            setActiveFilter(settings.productsDefaultActiveFilter);
          }
          if (settings.productsDefaultStockFilter !== undefined) {
            setStockFilter(settings.productsDefaultStockFilter);
          }
        }
        setSettingsLoaded(true);
      } catch (err) {
        console.warn("Could not load filter settings:", err);
        setSettingsLoaded(true); // Continue even if settings fail
      }
    };
    loadFilterSettings();
  }, []);

  // Save filter when changed
  const handleActiveFilterChange = async (value) => {
    setActiveFilter(value);
    setPageNumber(1);
    // Save to settings
    try {
      await pharmaApi.updateSettings({ productsDefaultActiveFilter: value });
    } catch (err) {
      console.warn("Could not save filter setting:", err);
    }
  };

  const handleStockFilterChange = async (value) => {
    setStockFilter(value);
    setPageNumber(1);
    // Save to settings
    try {
      await pharmaApi.updateSettings({ productsDefaultStockFilter: value });
    } catch (err) {
      console.warn("Could not save filter setting:", err);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Use PharmaFlow API
        const data = await pharmaApi.getCategories();
        setCategories(data || []);
      } catch (err) {
        toastError(err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    // Wait for settings to load before fetching products
    if (!settingsLoaded) return;

    const fetchProducts = async () => {
      setLoading(true);
      try {
        // Use PharmaFlow API
        const data = await pharmaApi.listProducts({
          searchParam,
          categoryId: categoryFilter || undefined,
          isActive: activeFilter === "" ? undefined : activeFilter === "true",
          requiresPrescription: prescriptionFilter || undefined,
          productType: productTypeFilter || undefined,
          activePrinciple: formulaFilter || undefined,
          stockFilter: stockFilter || undefined,
          cosmosEnrichmentStatus: enrichmentStatusFilter || undefined,
          pageNumber,
          limit: rowsPerPage,
        });
        dispatch({ type: "LOAD_PRODUCTS", payload: data.products || data || [] });
        setTotalCount(data.count || (data.products ? data.products.length : data.length));
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [settingsLoaded, searchParam, categoryFilter, activeFilter, prescriptionFilter, productTypeFilter, formulaFilter, stockFilter, enrichmentStatusFilter, pageNumber, rowsPerPage]);

  useEffect(() => {
    const companyId = user.companyId;
    const onPdvProduct = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PRODUCT", payload: data.product });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_PRODUCT", payload: data.productId });
      }
    };

    socket.on(`company-${companyId}-pdvProduct`, onPdvProduct);
    return () => {
      socket.off(`company-${companyId}-pdvProduct`, onPdvProduct);
    };
  }, [socket, user]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const refreshProducts = () => {
    setPageNumber(1);
    // Trigger re-fetch by toggling a state
    setSearchParam(prev => prev + " ");
    setTimeout(() => setSearchParam(prev => prev.trim()), 100);
  };

  const getStockDisplay = (product) => {
    const stock = product.stock;
    if (!stock) return { text: "-", className: "" };

    const available = stock.availableQuantity || 0;
    const isLow = available <= (stock.minQuantity || 5);

    return {
      text: available.toString(),
      className: isLow ? classes.lowStock : classes.normalStock,
    };
  };

  const getEnrichmentStatusChip = (product) => {
    const status = product.cosmosEnrichmentStatus;
    if (!status) return null;

    const label = enrichmentStatusLabels[status] || status;
    const icon = enrichmentStatusIcons[status] || "";
    const errorMessage = product.cosmosErrorMessage;

    let chipClass = classes.enrichmentChip;
    switch (status) {
      case "success":
        chipClass += ` ${classes.enrichmentSuccess}`;
        break;
      case "pending":
        chipClass += ` ${classes.enrichmentPending}`;
        break;
      case "not_found":
        chipClass += ` ${classes.enrichmentNotFound}`;
        break;
      case "error":
        chipClass += ` ${classes.enrichmentError}`;
        break;
      case "no_barcode":
        chipClass += ` ${classes.enrichmentNoBarcode}`;
        break;
      default:
        break;
    }

    const tooltipText = errorMessage
      ? `${label}: ${errorMessage}`
      : label;

    return (
      <Tooltip title={tooltipText}>
        <Chip
          label={`${icon} ${label}`}
          size="small"
          className={chipClass}
        />
      </Tooltip>
    );
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Catálogo de Produtos</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<EnrichIcon />}
            onClick={() => setEnrichmentModalOpen(true)}
            className={classes.enrichmentButton}
          >
            Enriquecer Dados
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={() => setImportModalOpen(true)}
            style={{ marginRight: 8 }}
          >
            Importar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm(null)}
          >
            Novo Produto
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <Box className={classes.filterContainer}>
          <TextField
            placeholder="Buscar por nome, código de barras, SKU..."
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={(e) => {
              setSearchParam(e.target.value);
              setPageNumber(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            className={classes.searchField}
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Grupo</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPageNumber(1);
              }}
              label="Grupo"
            >
              <MenuItem value="">Todos</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Subgrupo</InputLabel>
            <Select
              value={productTypeFilter}
              onChange={(e) => {
                setProductTypeFilter(e.target.value);
                setPageNumber(1);
              }}
              label="Subgrupo"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="medication">Medicamento</MenuItem>
              <MenuItem value="generic">Genérico</MenuItem>
              <MenuItem value="similar">Similar</MenuItem>
              <MenuItem value="cosmetic">Cosmético</MenuItem>
              <MenuItem value="hygiene">Higiene</MenuItem>
              <MenuItem value="other">Outros</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeFilter}
              onChange={(e) => handleActiveFilterChange(e.target.value)}
              label="Status"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="true">Ativos</MenuItem>
              <MenuItem value="false">Inativos</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Receita</InputLabel>
            <Select
              value={prescriptionFilter}
              onChange={(e) => {
                setPrescriptionFilter(e.target.value);
                setPageNumber(1);
              }}
              label="Receita"
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="none">Livre</MenuItem>
              <MenuItem value="simple">Receita Simples</MenuItem>
              <MenuItem value="double">Receita Dupla</MenuItem>
              <MenuItem value="special">Receita Especial</MenuItem>
              <MenuItem value="controlled">Controlado</MenuItem>
            </Select>
          </FormControl>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar por fórmula..."
            value={formulaFilter}
            onChange={(e) => {
              setFormulaFilter(e.target.value);
              setPageNumber(1);
            }}
            style={{ minWidth: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 18, color: "#999" }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
            <InputLabel>Estoque</InputLabel>
            <Select
              value={stockFilter}
              onChange={(e) => handleStockFilterChange(e.target.value)}
              label="Estoque"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="with">Com Estoque</MenuItem>
              <MenuItem value="without">Sem Estoque</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.enrichmentStatusFilter}>
            <InputLabel>Enriquecimento</InputLabel>
            <Select
              value={enrichmentStatusFilter}
              onChange={(e) => {
                setEnrichmentStatusFilter(e.target.value);
                setPageNumber(1);
              }}
              label="Enriquecimento"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="success">Enriquecidos</MenuItem>
              <MenuItem value="pending">Pendentes</MenuItem>
              <MenuItem value="not_found">Não Encontrado</MenuItem>
              <MenuItem value="error">Com Erro</MenuItem>
              <MenuItem value="no_barcode">Sem Código de Barras</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer className={classes.tableContainer}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeaderCell} style={{ width: 50 }}></TableCell>
                <TableCell className={classes.tableHeaderCell}>Produto</TableCell>
                <TableCell className={classes.tableHeaderCell}>Fórmula</TableCell>
                <TableCell className={classes.tableHeaderCell}>Fabricante</TableCell>
                <TableCell className={classes.tableHeaderCell}>Grupo</TableCell>
                <TableCell className={classes.tableHeaderCell} align="right">Preço</TableCell>
                <TableCell className={classes.tableHeaderCell} align="right">PMC</TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">Estoque</TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">Subgrupo</TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">Receita</TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">Status</TableCell>
                <TableCell className={classes.tableHeaderCell} align="center" style={{ width: 150 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRowSkeleton columns={12} />
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography color="textSecondary" style={{ padding: 20 }}>
                      {searchParam ? "Nenhum produto encontrado" : "Nenhum produto cadastrado"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => {
                  const stockInfo = getStockDisplay(product);
                  return (
                    <TableRow key={product.id} hover>
                      <TableCell style={{ width: 50 }}>
                        <Tooltip title={product.imageUrl ? "Clique para ampliar" : "Sem imagem"}>
                          <Avatar
                            src={product.thumbnailUrl || product.imageUrl}
                            variant="rounded"
                            className={`${classes.productImage} ${product.imageUrl ? classes.productImageClickable : ""}`}
                            onClick={(e) => product.imageUrl && handleOpenImagePreview(product, e)}
                          >
                            {product.imageUrl ? product.name?.charAt(0) : <ImageIcon />}
                          </Avatar>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <div className={classes.productName}>
                          {product.name}
                          {product.promoPrice && (
                            <Chip
                              label="PROMO"
                              size="small"
                              className={classes.promoChip}
                            />
                          )}
                          {product.isFeatured && (
                            <Chip
                              label="DESTAQUE"
                              size="small"
                              className={classes.featuredChip}
                            />
                          )}
                          {getEnrichmentStatusChip(product)}
                        </div>
                        <div className={classes.productDetails}>
                          {product.presentation && <span>{product.presentation}</span>}
                          {product.presentation && product.dosage && <span> - </span>}
                          {product.dosage && <span>{product.dosage}</span>}
                        </div>
                        {(product.barcode || product.sku) && (
                          <div className={classes.barcodeText}>
                            {product.barcode && <span>EAN: {product.barcode}</span>}
                            {product.barcode && product.sku && <span> | </span>}
                            {product.sku && <span>SKU: {product.sku}</span>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span style={{ fontSize: 12 }}>
                          {product.activePrinciple || "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={classes.manufacturerText}>
                          {product.manufacturer || "-"}
                        </span>
                      </TableCell>
                      <TableCell>{product.category?.name || "-"}</TableCell>
                      <TableCell align="right" className={classes.priceCell}>
                        {product.promoPrice ? (
                          <>
                            <span style={{ textDecoration: "line-through", color: "#999", fontSize: 11 }}>
                              {formatCurrency(product.price)}
                            </span>
                            <br />
                            <span style={{ color: "#4caf50", fontWeight: "bold" }}>
                              {formatCurrency(product.promoPrice)}
                            </span>
                          </>
                        ) : (
                          <span style={{ fontWeight: 500 }}>{formatCurrency(product.price)}</span>
                        )}
                      </TableCell>
                      <TableCell align="right" className={classes.priceCell}>
                        <span style={{ fontSize: 12, color: "#666" }}>
                          {product.maxPrice ? formatCurrency(product.maxPrice) : "-"}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        <span className={`${classes.stockCell} ${stockInfo.className}`}>
                          {stockInfo.text}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={productTypeLabels[product.productType] || "Outros"}
                          size="small"
                          variant="outlined"
                          style={{ fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={prescriptionLabels[product.requiresPrescription] || "Livre"}>
                          <Chip
                            label={prescriptionLabels[product.requiresPrescription]?.substring(0, 8) || "Livre"}
                            size="small"
                            className={classes.prescriptionChip}
                            color={prescriptionColors[product.requiresPrescription] || "default"}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={product.isActive ? "Ativo" : "Inativo"}
                          size="small"
                          className={product.isActive ? classes.activeChip : classes.inactiveChip}
                        />
                      </TableCell>
                      <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                        <Tooltip title="Ver detalhes">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDetail(product)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenForm(product)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            className={classes.deleteButton}
                            onClick={() => handleOpenDelete(product)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={pageNumber - 1}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </Paper>
      <ProductImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={refreshProducts}
      />
      <ProductDetailModal
        open={detailModalOpen}
        onClose={handleCloseDetail}
        product={selectedProduct}
        classes={classes}
        formatCurrency={formatCurrency}
      />
      <ProductFormModal
        open={formModalOpen}
        onClose={handleCloseForm}
        product={selectedProduct}
        categories={categories}
        onSave={handleSaveProduct}
        classes={classes}
        onManageImage={handleOpenImage}
      />
      <ConfirmationModal
        open={deleteModalOpen}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteProduct}
        title="Excluir Produto"
      >
        Tem certeza que deseja excluir o produto <strong>{selectedProduct?.name}</strong>?
        Esta ação não pode ser desfeita.
      </ConfirmationModal>
      <ProductImageModal
        open={imageModalOpen}
        onClose={handleCloseImage}
        product={selectedProduct}
        onUpdate={handleImageUpdate}
      />
      {/* Modal de Preview da Imagem Ampliada */}
      <Dialog
        open={imagePreviewOpen}
        onClose={handleCloseImagePreview}
        maxWidth={false}
        className={classes.imagePreviewDialog}
      >
        <DialogContent className={classes.imagePreviewContent}>
          {previewProduct && (
            <Box style={{ position: "relative", display: "inline-block" }}>
              <IconButton
                onClick={handleCloseImagePreview}
                className={classes.imagePreviewClose}
                size="small"
              >
                <CloseIcon />
              </IconButton>
              <img
                src={previewProduct.imageUrl}
                alt={previewProduct.name}
                className={classes.imagePreviewImg}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Enriquecimento de Dados */}
      <ProductEnrichmentModal
        open={enrichmentModalOpen}
        onClose={() => setEnrichmentModalOpen(false)}
        classes={classes}
      />

    </MainContainer>
  );
};

export default PdvProducts;
